/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { FboType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface FboProps {
  id: string;
  name: string;
  fuelBrand: string | null;
  airportId: string;
  fboType: FboType;
  sourceSystemId: number | null;
  locationId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class FboRecord extends Record<FboProps>({
  id: null as any,
  name: null as any,
  fuelBrand: null as any,
  airportId: null as any,
  fboType: null as any,
  sourceSystemId: null as any,
  locationId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    fuelBrand: string | null;
    airportId: string;
    fboType: FboType;
    sourceSystemId: number | null;
    locationId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): FboRecord {
    if (json == null) {
      throw new Error('FboRecord.fromJS: parameter cannot be null');
    }
    return new FboRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'FboJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'FboJson.name'),
      fuelBrand: json.fuelBrand,
      airportId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'airportId', '00000000-0000-0000-0000-000000000000'),
        'FboJson.airportId',
      ),
      fboType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'fboType', FboType.FlightSupportOrganisation),
        'FboJson.fboType',
      ),
      sourceSystemId: json.sourceSystemId,
      locationId: json.locationId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    fuelBrand: string | null;
    airportId: string;
    fboType: FboType;
    sourceSystemId: number | null;
    locationId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [FboRecord, Error[]] {
    if (json == null) {
      throw new Error('FboRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new FboRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'FboJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'FboJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fuelBrand: tryOrDefault(
        () => json.fuelBrand,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      airportId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'airportId', '00000000-0000-0000-0000-000000000000'),
            'FboJson.airportId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fboType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'fboType', FboType.FlightSupportOrganisation),
            'FboJson.fboType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sourceSystemId: tryOrDefault(
        () => json.sourceSystemId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () => json.locationId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: FboProps) {
    super(props);
    if (props == null) {
      throw new Error('FboRecord constructor parameter cannot be null');
    }
  }
}

export abstract class FboViewModelBase implements FboProps {
  protected readonly _props: FboProps;
  private _json?: FboProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get fuelBrand(): string | null {
    return this._props.fuelBrand;
  }
  get airportId(): string {
    return this._props.airportId;
  }
  get fboType(): FboType {
    return this._props.fboType;
  }
  get sourceSystemId(): number | null {
    return this._props.sourceSystemId;
  }
  get locationId(): string | null {
    return this._props.locationId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isFBO() {
    return this._props.fboType === FboType.FBO;
  }
  public get isFlightSupportOrganisation() {
    return this._props.fboType === FboType.FlightSupportOrganisation;
  }
  public get isFuelOnly() {
    return this._props.fboType === FboType.FuelOnly;
  }
  public get isHandler() {
    return this._props.fboType === FboType.Handler;
  }
  public get isLineRampEngineering() {
    return this._props.fboType === FboType.LineRampEngineering;
  }
  public get isSupervisingAgent() {
    return this._props.fboType === FboType.SupervisingAgent;
  }

  public isAnyFboType(...fboType: FboType[]) {
    return fboType.includes(this._props.fboType);
  }

  constructor(props: FboProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        fuelBrand: this._props.fuelBrand,
        airportId: this._props.airportId,
        fboType: this._props.fboType,
        sourceSystemId: this._props.sourceSystemId,
        locationId: this._props.locationId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllFboEntities = (state: AppState) => state.entityStores.fbo.entities;

export const selectFboById = (id: string) => (state: AppState) =>
  state.entityStores.fbo.entities.get(id);

export const selectFboMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.fbo.meta.get(id);

export const selectFboAndMetadataById = (id: string) =>
  createSelector(selectFboById(id), selectFboMetadataById(id), (fbo, meta) => ({ fbo, meta }));

export function selectFboEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllFboEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, FboRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectFboEntitiesByName(name: string) {
  return createSelector(selectAllFboEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectFboEntitiesByFuelBrand(fuelBrand: string) {
  return createSelector(selectAllFboEntities, (entities) =>
    entities.filter((e) => e.fuelBrand === fuelBrand).valueSeq(),
  );
}

export function selectFboEntitiesByAirportId(airportId: string) {
  return createSelector(selectAllFboEntities, (entities) =>
    entities.filter((e) => e.airportId === airportId).valueSeq(),
  );
}

export function selectFboEntitiesByFboType(fboType: FboType) {
  return createSelector(selectAllFboEntities, (entities) =>
    entities.filter((e) => e.fboType === fboType).valueSeq(),
  );
}

export function selectFboEntitiesBySourceSystemId(sourceSystemId: number) {
  return createSelector(selectAllFboEntities, (entities) =>
    entities.filter((e) => e.sourceSystemId === sourceSystemId).valueSeq(),
  );
}

export function selectFboEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllFboEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}
//#endregion Selectors
